/**
 * Theme Definitions
 * Complete theme configurations for WhenCanIQuit
 */

import type { 
  ThemeDefinition, 
  CompleteTheme, 
  AssetTypeColors, 
  FinancialColors, 
  ThemeAwareChartConfig 
} from './types';

// Helper function to create OKLCH color values
const oklch = (l: number, c: number, h: number, a?: number) => 
  a !== undefined ? `oklch(${l} ${c} ${h} / ${a})` : `oklch(${l} ${c} ${h})`;

// Default Theme (Neutral/Grayscale)
const defaultTheme: CompleteTheme = {
  name: 'default',
  displayName: 'Default',
  description: 'Clean neutral theme with professional aesthetics',
  colorScheme: 'default',
  light: {
    background: oklch(1, 0, 0),
    foreground: oklch(0.141, 0.005, 285.823),
    card: oklch(1, 0, 0),
    cardForeground: oklch(0.141, 0.005, 285.823),
    popover: oklch(1, 0, 0),
    popoverForeground: oklch(0.141, 0.005, 285.823),
    primary: oklch(0.55, 0.26, 262.34),
    primaryForeground: oklch(0.985, 0, 0),
    secondary: oklch(0.967, 0.001, 286.375),
    secondaryForeground: oklch(0.21, 0.006, 285.885),
    muted: oklch(0.967, 0.001, 286.375),
    mutedForeground: oklch(0.552, 0.016, 285.938),
    accent: oklch(0.967, 0.001, 286.375),
    accentForeground: oklch(0.21, 0.006, 285.885),
    destructive: oklch(0.577, 0.245, 27.325),
    destructiveForeground: oklch(0.985, 0, 0),
    border: oklch(0.92, 0.004, 286.32),
    input: oklch(0.92, 0.004, 286.32),
    ring: oklch(0.55, 0.26, 262.34),
    chart1: oklch(0.488, 0.243, 264.376),
    chart2: oklch(0.696, 0.17, 162.48),
    chart3: oklch(0.769, 0.188, 70.08),
    chart4: oklch(0.627, 0.265, 303.9),
    chart5: oklch(0.645, 0.246, 16.439),
    sidebar: oklch(0.985, 0, 0),
    sidebarForeground: oklch(0.141, 0.005, 285.823),
    sidebarPrimary: oklch(0.723, 0.219, 149.579),
    sidebarPrimaryForeground: oklch(0.982, 0.018, 155.826),
    sidebarAccent: oklch(0.967, 0.001, 286.375),
    sidebarAccentForeground: oklch(0.21, 0.006, 285.885),
    sidebarBorder: oklch(0.92, 0.004, 286.32),
    sidebarRing: oklch(0.723, 0.219, 149.579),
    success: oklch(0.65, 0.18, 142),
    successForeground: oklch(0.985, 0, 0),
    warning: oklch(0.75, 0.2, 85),
    warningForeground: oklch(0.145, 0, 0),
    info: oklch(0.6, 0.15, 220),
    infoForeground: oklch(0.985, 0, 0),
  },
  dark: {
    background: oklch(0.15, 0.016, 265.75),
    foreground: oklch(0.985, 0, 0),
    card: oklch(0.15, 0.016, 265.75),
    cardForeground: oklch(0.985, 0, 0),
    popover: oklch(0.15, 0.016, 265.75),
    popoverForeground: oklch(0.985, 0, 0),
    primary: oklch(0.696, 0.17, 265.75),
    primaryForeground: oklch(0.15, 0.016, 265.75),
    secondary: oklch(0.25, 0.015, 265.75),
    secondaryForeground: oklch(0.985, 0, 0),
    muted: oklch(0.25, 0.015, 265.75),
    mutedForeground: oklch(0.65, 0.015, 265.75),
    accent: oklch(0.25, 0.015, 265.75),
    accentForeground: oklch(0.985, 0, 0),
    destructive: oklch(0.577, 0.245, 27.325),
    destructiveForeground: oklch(0.985, 0, 0),
    border: oklch(0.25, 0.015, 265.75),
    input: oklch(0.25, 0.015, 265.75),
    ring: oklch(0.696, 0.17, 265.75),
    chart1: oklch(0.488, 0.243, 264.376),
    chart2: oklch(0.696, 0.17, 162.48),
    chart3: oklch(0.769, 0.188, 70.08),
    chart4: oklch(0.627, 0.265, 303.9),
    chart5: oklch(0.645, 0.246, 16.439),
    sidebar: oklch(0.15, 0.016, 265.75),
    sidebarForeground: oklch(0.985, 0, 0),
    sidebarPrimary: oklch(0.696, 0.17, 265.75),
    sidebarPrimaryForeground: oklch(0.15, 0.016, 265.75),
    sidebarAccent: oklch(0.25, 0.015, 265.75),
    sidebarAccentForeground: oklch(0.985, 0, 0),
    sidebarBorder: oklch(0.25, 0.015, 265.75),
    sidebarRing: oklch(0.696, 0.17, 265.75),
    success: oklch(0.7, 0.2, 142),
    successForeground: oklch(0.15, 0, 0),
    warning: oklch(0.8, 0.25, 85),
    warningForeground: oklch(0.15, 0, 0),
    info: oklch(0.7, 0.2, 220),
    infoForeground: oklch(0.15, 0, 0),
  },
  assetColors: {
    light: {
      stocks: oklch(0.55, 0.2, 220),       // Blue
      crypto: oklch(0.7, 0.25, 45),        // Orange
      savings: oklch(0.65, 0.18, 142),     // Green
      realEstate: oklch(0.6, 0.2, 280),    // Purple
      bonds: oklch(0.6, 0.15, 200),        // Cyan
      mutualFunds: oklch(0.65, 0.22, 30),  // Orange-red
      commodities: oklch(0.7, 0.2, 120),   // Lime
      cash: oklch(0.5, 0.05, 0),           // Gray
      mortgage: oklch(0.6, 0.22, 15),      // Red
      loan: oklch(0.65, 0.2, 320),         // Pink
      rentalPayment: oklch(0.7, 0.25, 45), // Orange
      rentalIncome: oklch(0.65, 0.18, 142) // Green
    },
    dark: {
      stocks: oklch(0.7, 0.25, 220),       // Brighter blue
      crypto: oklch(0.8, 0.3, 45),         // Brighter orange
      savings: oklch(0.75, 0.22, 142),     // Brighter green
      realEstate: oklch(0.75, 0.25, 280),  // Brighter purple
      bonds: oklch(0.75, 0.2, 200),        // Brighter cyan
      mutualFunds: oklch(0.8, 0.27, 30),   // Brighter orange-red
      commodities: oklch(0.8, 0.25, 120),  // Brighter lime
      cash: oklch(0.6, 0.08, 0),           // Lighter gray
      mortgage: oklch(0.75, 0.27, 15),     // Brighter red
      loan: oklch(0.8, 0.25, 320),         // Brighter pink
      rentalPayment: oklch(0.8, 0.3, 45),  // Brighter orange
      rentalIncome: oklch(0.75, 0.22, 142) // Brighter green
    }
  },
  financialColors: {
    light: {
      positive: oklch(0.65, 0.18, 142),    // Green
      negative: oklch(0.6, 0.22, 15),      // Red
      neutral: oklch(0.5, 0.05, 0),        // Gray
      growth: oklch(0.65, 0.18, 142),      // Green
      loss: oklch(0.6, 0.22, 15),          // Red
      debt: oklch(0.6, 0.22, 15),          // Red
      income: oklch(0.65, 0.18, 142),      // Green
      expense: oklch(0.7, 0.25, 45)        // Orange
    },
    dark: {
      positive: oklch(0.75, 0.22, 142),    // Brighter green
      negative: oklch(0.75, 0.27, 15),     // Brighter red
      neutral: oklch(0.6, 0.08, 0),        // Lighter gray
      growth: oklch(0.75, 0.22, 142),      // Brighter green
      loss: oklch(0.75, 0.27, 15),         // Brighter red
      debt: oklch(0.75, 0.27, 15),         // Brighter red
      income: oklch(0.75, 0.22, 142),      // Brighter green
      expense: oklch(0.8, 0.3, 45)         // Brighter orange
    }
  },
  chartConfig: {
    light: {
      colors: {
        stocks: oklch(0.55, 0.26, 220),
        crypto: oklch(0.6, 0.28, 45),
        savings: oklch(0.65, 0.24, 142),
        realEstate: oklch(0.7, 0.25, 280),
        bonds: oklch(0.55, 0.26, 200),
        mutualFunds: oklch(0.6, 0.28, 30),
        commodities: oklch(0.65, 0.24, 120),
        cash: oklch(0.58, 0.25, 15),
        mortgage: oklch(0.6, 0.25, 15),
        loan: oklch(0.7, 0.25, 320),
        rentalPayment: oklch(0.58, 0.25, 45),
        rentalIncome: oklch(0.65, 0.24, 142)
      },
      background: oklch(1, 0, 0),
      text: oklch(0.145, 0, 0),
      grid: oklch(0.95, 0, 0),
      axis: oklch(0.6, 0, 0),
      tooltip: {
        background: oklch(1, 0, 0),
        border: oklch(0.922, 0, 0),
        text: oklch(0.145, 0, 0)
      }
    },
    dark: {
      colors: {
        stocks: oklch(0.7, 0.3, 220),
        crypto: oklch(0.8, 0.32, 45),
        savings: oklch(0.75, 0.28, 142),
        realEstate: oklch(0.8, 0.3, 280),
        bonds: oklch(0.7, 0.3, 200),
        mutualFunds: oklch(0.8, 0.32, 30),
        commodities: oklch(0.75, 0.28, 120),
        cash: oklch(0.75, 0.3, 15),
        mortgage: oklch(0.75, 0.3, 15),
        loan: oklch(0.8, 0.3, 320),
        rentalPayment: oklch(0.75, 0.3, 45),
        rentalIncome: oklch(0.75, 0.28, 142)
      },
      background: oklch(0.145, 0, 0),
      text: oklch(0.985, 0, 0),
      grid: oklch(0.25, 0, 0),
      axis: oklch(0.5, 0, 0),
      tooltip: {
        background: oklch(0.205, 0, 0),
        border: oklch(1, 0, 0, 0.1),
        text: oklch(0.985, 0, 0)
      }
    }
  }
};

// Blue Theme (Finance/Professional)
const blueTheme: CompleteTheme = {
  ...defaultTheme,
  name: 'blue',
  displayName: 'Ocean Blue',
  description: 'Professional blue theme inspired by financial institutions',
  colorScheme: 'blue',
  light: {
    ...defaultTheme.light,
    primary: oklch(0.55, 0.2, 220),
    primaryForeground: oklch(0.985, 0, 0),
    ring: oklch(0.55, 0.2, 220),
    chart1: oklch(0.55, 0.2, 220),
    chart2: oklch(0.65, 0.15, 200),
    chart3: oklch(0.6, 0.18, 240),
    chart4: oklch(0.7, 0.12, 260),
    chart5: oklch(0.45, 0.25, 200),
    sidebarPrimary: oklch(0.55, 0.2, 220),
  },
  dark: {
    ...defaultTheme.dark,
    primary: oklch(0.7, 0.25, 220),
    primaryForeground: oklch(0.145, 0, 0),
    ring: oklch(0.7, 0.25, 220),
    chart1: oklch(0.7, 0.25, 220),
    chart2: oklch(0.75, 0.2, 200),
    chart3: oklch(0.72, 0.22, 240),
    chart4: oklch(0.8, 0.15, 260),
    chart5: oklch(0.6, 0.3, 200),
    sidebarPrimary: oklch(0.7, 0.25, 220),
  }
};

// Green Theme (Growth/Prosperity)
const greenTheme: CompleteTheme = {
  ...defaultTheme,
  name: 'green',
  displayName: 'Prosperity Green',
  description: 'Growth-focused green theme representing financial success',
  colorScheme: 'green',
  light: {
    ...defaultTheme.light,
    primary: oklch(0.65, 0.18, 142),
    primaryForeground: oklch(0.985, 0, 0),
    ring: oklch(0.65, 0.18, 142),
    chart1: oklch(0.65, 0.18, 142),
    chart2: oklch(0.7, 0.15, 120),
    chart3: oklch(0.6, 0.2, 160),
    chart4: oklch(0.75, 0.12, 100),
    chart5: oklch(0.55, 0.22, 180),
    sidebarPrimary: oklch(0.65, 0.18, 142),
  },
  dark: {
    ...defaultTheme.dark,
    primary: oklch(0.75, 0.22, 142),
    primaryForeground: oklch(0.145, 0, 0),
    ring: oklch(0.75, 0.22, 142),
    chart1: oklch(0.75, 0.22, 142),
    chart2: oklch(0.8, 0.18, 120),
    chart3: oklch(0.72, 0.25, 160),
    chart4: oklch(0.85, 0.15, 100),
    chart5: oklch(0.65, 0.27, 180),
    sidebarPrimary: oklch(0.75, 0.22, 142),
  }
};

// Purple Theme (Premium/Luxury)
const purpleTheme: CompleteTheme = {
  ...defaultTheme,
  name: 'purple',
  displayName: 'Royal Purple',
  description: 'Premium purple theme for a sophisticated experience',
  colorScheme: 'purple',
  light: {
    ...defaultTheme.light,
    primary: oklch(0.6, 0.2, 280),
    primaryForeground: oklch(0.985, 0, 0),
    ring: oklch(0.6, 0.2, 280),
    chart1: oklch(0.6, 0.2, 280),
    chart2: oklch(0.65, 0.18, 300),
    chart3: oklch(0.55, 0.22, 260),
    chart4: oklch(0.7, 0.15, 320),
    chart5: oklch(0.5, 0.25, 240),
    sidebarPrimary: oklch(0.6, 0.2, 280),
  },
  dark: {
    ...defaultTheme.dark,
    primary: oklch(0.75, 0.25, 280),
    primaryForeground: oklch(0.145, 0, 0),
    ring: oklch(0.75, 0.25, 280),
    chart1: oklch(0.75, 0.25, 280),
    chart2: oklch(0.78, 0.22, 300),
    chart3: oklch(0.7, 0.27, 260),
    chart4: oklch(0.8, 0.18, 320),
    chart5: oklch(0.65, 0.3, 240),
    sidebarPrimary: oklch(0.75, 0.25, 280),
  }
};

// All available themes
export const THEMES: Record<string, CompleteTheme> = {
  default: defaultTheme,
  blue: blueTheme,
  green: greenTheme,
  purple: purpleTheme,
  // Additional themes can be added here
};

// Theme utilities
export const getTheme = (colorScheme: string): CompleteTheme => {
  return THEMES[colorScheme] || THEMES.default;
};

export const getAvailableThemes = (): CompleteTheme[] => {
  return Object.values(THEMES);
};

export const getThemeNames = (): string[] => {
  return Object.keys(THEMES);
};
